import React, { useState, useEffect } from 'react';
import { 
  FaBars, FaTimes, FaServer, FaNetworkWired, FaCode, 
  FaEye, FaBullseye, FaMapMarkerAlt, FaEnvelope, FaChevronRight,
  FaBuilding, FaHandshake, FaCheckCircle, FaPhone, FaGlobe,
  FaSpinner, FaCheck, FaExclamationTriangle
} from 'react-icons/fa';
import './App.css';

const API_URL = 'http://localhost:5000/api';

const App = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'partners', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setFormStatus({
        loading: false,
        success: false,
        error: 'Please fill in all required fields.'
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setFormStatus({
        loading: false,
        success: false,
        error: 'Please enter a valid email address.'
      });
      return;
    }

    setFormStatus({ loading: true, success: false, error: null });

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setFormStatus({
          loading: false,
          success: true,
          error: null
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setFormStatus(prev => ({ ...prev, success: false }));
        }, 5000);
      } else {
        throw new Error(result.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        loading: false,
        success: false,
        error: error.message || 'Network error. Please try again.'
      });
    }
  };

  const services = [
    {
      id: 1,
      icon: <FaServer />,
      title: "Data Center Solutions",
      items: [
        "Cloud and hybrid data center deployments",
        "Disaster Recovery and Business Continuity solutions",
        "Colocation and managed hosting"
      ]
    },
    {
      id: 2,
      icon: <FaNetworkWired />,
      title: "Networking Solutions",
      items: [
        "Enterprise network architecture and implementation",
        "SD-WAN, VPN, LAN/WAN optimization",
        "Network security and firewall management",
        "IoT and Industrial Network Systems"
      ]
    },
    {
      id: 3,
      icon: <FaCode />,
      title: "Software Development",
      items: [
        "Custom web and mobile application development",
        "ERP, CRM, and enterprise system integration",
        "Cloud-native app development (AWS, Azure, GCP)",
        "AI/ML-powered business solutions"
      ]
    }
  ];

  const partners = [
    {
      id: 1,
      name: "Arifpay Financial Technology",
      description: "Fintech solutions and payment gateway integration"
    },
    {
      id: 2,
      name: "Spectrum Engineering plc",
      description: "Engineering infrastructure and digital transformation"
    },
    {
      id: 3,
      name: "Newave Hi-Tech solutions plc",
      description: "Advanced technology solutions and IT consulting"
    },
    {
      id: 4,
      name: "Lehulum Technologies PLC",
      description: "Enterprise software development and system integration"
    },
    {
      id: 5,
      name: "FenanPay Financial Technology",
      description: "Digital payment solutions and financial technology"
    }
  ];

  const contactInfo = [
    {
      id: 1,
      icon: <FaMapMarkerAlt />,
      title: "Address",
      details: "Addis Ababa, Ethiopia",
      link: null
    },
    {
      id: 2,
      icon: <FaPhone />,
      title: "Phone",
      details: "+251 969 931 000",
      link: "tel:+251969931000"
    },
    {
      id: 3,
      icon: <FaEnvelope />,
      title: "Email",
      details: "contact@sunitsolution.com",
      link: "mailto:contact@sunitsolution.com"
    },
    {
      id: 4,
      icon: <FaGlobe />,
      title: "Website",
      details: "www.sunitsolution.com",
      link: "#"
    }
  ];

  return (
    <div className="App">
      {/* Header & Navigation */}
      <header className="header">
        <div className="container">
          <nav className="navbar">
            <div className="logo" onClick={() => scrollToSection('home')}>
              <img 
                src="/photo_2025-12-09_21-08-49.jpg" 
                alt="Sun IT Solutions Logo" 
                className="logo-image"
              />
            </div>
            
            <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
              <li>
                <a 
                  className={activeSection === 'home' ? 'active' : ''}
                  onClick={() => scrollToSection('home')}
                >
                  Home
                </a>
              </li>
              <li>
                <a 
                  className={activeSection === 'about' ? 'active' : ''}
                  onClick={() => scrollToSection('about')}
                >
                  About Us
                </a>
              </li>
              <li>
                <a 
                  className={activeSection === 'services' ? 'active' : ''}
                  onClick={() => scrollToSection('services')}
                >
                  Services
                </a>
              </li>
              <li>
                <a 
                  className={activeSection === 'partners' ? 'active' : ''}
                  onClick={() => scrollToSection('partners')}
                >
                  Our Partners
                </a>
              </li>
              <li>
                <a 
                  className={activeSection === 'contact' ? 'active' : ''}
                  onClick={() => scrollToSection('contact')}
                >
                  Contact
                </a>
              </li>
            </ul>
            
            <div className="mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-logo-container">
            <img 
              src="/photo_2025-12-09_21-08-49.jpg" 
              alt="Sun IT Solutions Logo" 
              className="hero-logo"
            />
          </div>
          <h1>Innovative IT Solutions for a Digital World</h1>
          <p>Sun IT Solution PLC has established in 2015 with rapidly emerged as a leading solution provider in the data center, networking, and software development sectors.</p>
          <button className="btn btn-primary" onClick={() => scrollToSection('about')}>
            Learn More
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="section-title">
            <div className="section-logo">
              <img 
                src="/photo_2025-12-09_21-08-49.jpg" 
                alt="Sun IT Solutions Logo" 
                className="section-logo-image"
              />
            </div>
            <h2>About Us</h2>
            <p>Learn about our journey, vision, and mission to become a globally trusted IT infrastructure partner</p>
          </div>
          
          <div className="about-content">
            <div className="about-text">
              <h3>Company Overview</h3>
              <p><strong>Sun IT Solution PLC</strong>, established in <strong>2015</strong>, has rapidly emerged as a leading solution provider in the <strong>data center, networking, and software development sectors</strong>.</p>
              <p>Built on a strong foundation of <strong>innovation, reliability, and client-centric solutions</strong>, we have successfully grown from a regional service provider into a <strong>globally trusted IT infrastructure partner</strong>.</p>
            </div>
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80" alt="Sun IT Solutions Team" />
            </div>
          </div>
          
          <div className="vision-mission">
            <div className="vision">
              <h3><FaEye /> Our Vision</h3>
              <p>To be the world's most trusted and innovative IT infrastructure Company, empowering businesses with intelligent, secure, and scalable technology solutions.</p>
            </div>
            
            <div className="mission">
              <h3><FaBullseye /> Our Mission</h3>
              <p>At Sun IT Solution PLC, our mission is to deliver cutting-edge data center, networking, and software development services that drive digital transformation. We are committed to innovation, operational excellence, and client success providing reliable, future-ready solutions that enable organizations to thrive in a connected world.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services">
        <div className="container">
          <div className="section-title">
            <h2>Our Core Services</h2>
            <p>Comprehensive IT solutions tailored to meet your business needs and drive growth</p>
          </div>
          
          <div className="services-container">
            {services.map(service => (
              <div key={service.id} className="service-card">
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <ul>
                  {service.items.map((item, index) => (
                    <li key={index}>
                      <FaChevronRight className="list-icon" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="partners">
        <div className="container">
          <div className="section-title">
            <h2>Our Successful Projects & Partners</h2>
            <p>We are proud to have successfully deployed projects for industry leaders across various sectors</p>
          </div>
          
          <div className="partners-container">
            {partners.map(partner => (
              <div key={partner.id} className="partner-card">
                <div className="partner-icon">
                  <FaBuilding />
                </div>
                <h3>{partner.name}</h3>
                <p>{partner.description}</p>
                <div className="partner-status">
                  <FaCheckCircle className="check-icon" />
                  <span>Successfully Deployed</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="partners-summary">
            <FaHandshake className="summary-icon" />
            <p>
              We take pride in our collaborative approach and the successful delivery of IT solutions 
              that have helped our partners achieve their digital transformation goals. 
              Each project represents our commitment to excellence and client satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-title">
            <h2>Contact Us</h2>
            <p>Get in touch with our team to discuss how we can help transform your IT infrastructure</p>
          </div>
          
          <div className="contact-container">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>Reach out to us for any inquiries about our services or to discuss your specific IT needs. Our team is ready to assist you.</p>
              
              <div className="contact-details">
                {contactInfo.map(info => (
                  <div key={info.id} className="contact-item">
                    <div className="contact-icon">
                      {info.icon}
                    </div>
                    <div>
                      <h4>{info.title}</h4>
                      {info.link ? (
                        <a 
                          href={info.link} 
                          className="contact-link"
                          target={info.title === "Website" ? "_blank" : "_self"}
                          rel={info.title === "Website" ? "noopener noreferrer" : ""}
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p>{info.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="contact-hours">
                <h4>Business Hours</h4>
                <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                <p>Saturday: 9:00 AM - 1:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
            
            <div className="contact-form">
              <h3>Send Message</h3>
              
              {/* Status Messages */}
              {formStatus.success && (
                <div className="alert alert-success">
                  <FaCheck /> Thank you for your message! We will get back to you soon.
                </div>
              )}
              
              {formStatus.error && (
                <div className="alert alert-error">
                  <FaExclamationTriangle /> {formStatus.error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    className="form-control" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                    disabled={formStatus.loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    className="form-control" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                    disabled={formStatus.loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    className="form-control" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={formStatus.loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <input 
                    type="text" 
                    id="subject" 
                    className="form-control" 
                    value={formData.subject}
                    onChange={handleInputChange}
                    required 
                    disabled={formStatus.loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea 
                    id="message" 
                    className="form-control" 
                    value={formData.message}
                    onChange={handleInputChange}
                    required 
                    disabled={formStatus.loading}
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={formStatus.loading}
                >
                  {formStatus.loading ? (
                    <>
                      <FaSpinner className="spinner" /> Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo-container">
                <img 
                  src="/photo_2025-12-09_21-08-49.jpg" 
                  alt="Sun IT Solutions Logo" 
                  className="footer-logo"
                />
                <div className="footer-logo-text">
                  <h2>Sun IT Solutions</h2>
                  <p className="footer-subtitle">Technology Solutions PLC</p>
                </div>
              </div>
              <div className="footer-contact">
                <p><FaPhone /> +251 969 931 000</p>
                <p><FaEnvelope /> contact@sunitsolution.com</p>
              </div>
            </div>
            
            <div className="footer-links">
              <h3>Quick Links</h3>
              <ul>
                <li><a onClick={() => scrollToSection('home')}>Home</a></li>
                <li><a onClick={() => scrollToSection('about')}>About Us</a></li>
                <li><a onClick={() => scrollToSection('services')}>Services</a></li>
                <li><a onClick={() => scrollToSection('partners')}>Our Partners</a></li>
                <li><a onClick={() => scrollToSection('contact')}>Contact</a></li>
              </ul>
            </div>
            
            <div className="footer-links">
              <h3>Services</h3>
              <ul>
                <li><a onClick={() => scrollToSection('services')}>Data Center Solutions</a></li>
                <li><a onClick={() => scrollToSection('services')}>Networking Solutions</a></li>
                <li><a onClick={() => scrollToSection('services')}>Software Development</a></li>
              </ul>
            </div>
          </div>
          
          <div className="copyright">
            <div className="copyright-logo">
              <img 
                src="/photo_2025-12-09_21-08-49.jpg" 
                alt="Sun IT Solutions Logo" 
                className="copyright-logo-image"
              />
            </div>
            <p>&copy; {new Date().getFullYear()} Sun IT Solution PLC. All Rights Reserved. | Phone: +251 969 931 000</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;